import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import { Calendar, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A date field with a real calendar, replacing `<input type="date">`.
 *
 * ## Why not the native input
 *
 * It renders differently in every browser, its picker cannot be styled at all, and on desktop
 * Firefox and Safari it is barely a picker — the user is typing into three fiddly segments in
 * an order that follows the OS locale rather than the one the screen is labelled in. For dates
 * that are usually months or years away (an election, a milestone, a filing deadline) that is
 * a lot of typing to land on the right day.
 *
 * ## The value is always an ISO date string
 *
 * `yyyy-MM-dd`, exactly what `<input type="date">` produced and exactly what the API takes, so
 * swapping this in never changes a request body. `''` means empty.
 *
 * ## Dates here are calendar dates, not instants
 *
 * Everything below constructs and reads dates in LOCAL time and formats them by hand. Going
 * through `toISOString()` would convert to UTC first, and for anyone east of Greenwich —
 * Nairobi is UTC+3 — that turns "the 1st" into "the 31st" for the whole evening. That bug is
 * why there is no `Date.prototype.toISOString` anywhere in this file.
 */
export function DatePicker({
  value, onChange, disabled, min, max, id, className, placeholder = 'Select a date', clearable = true,
}: {
  /** `yyyy-MM-dd`, or '' */
  value: string
  onChange: (isoDate: string) => void
  disabled?: boolean
  /** `yyyy-MM-dd` bounds, matching the native input's min/max. */
  min?: string
  max?: string
  id?: string
  className?: string
  placeholder?: string
  clearable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const popover = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const selected = parseIsoDate(value)

  // Rendered into a portal so the calendar is never clipped by a drawer, a dialog or a
  // table's overflow container — the three places these fields actually live.
  useLayoutEffect(() => {
    if (!open || !anchor.current) return
    const r = anchor.current.getBoundingClientRect()
    const height = 340
    const below = window.innerHeight - r.bottom
    setPos({
      top: below < height && r.top > height ? r.top - height : r.bottom + 4,
      left: Math.min(r.left, window.innerWidth - 300),
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!popover.current?.contains(t) && !anchor.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    // `true` — capture. A click inside a drawer that stops propagation would otherwise
    // leave the calendar open forever.
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        id={id}
        ref={anchor}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'erp-input w-full flex items-center gap-2 text-left',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className={cn('flex-1 truncate', !selected && 'text-muted-foreground')}>
          {selected ? formatDisplay(selected) : placeholder}
        </span>
        {clearable && value && !disabled && (
          <X
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
            role="button"
            aria-label="Clear date"
            onClick={(e) => { e.stopPropagation(); onChange('') }}
          />
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={popover}
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-[100] rounded-md border bg-popover p-2 shadow-lg axis-daypicker"
        >
          <DayPicker
            mode="single"
            selected={selected}
            defaultMonth={selected ?? undefined}
            onSelect={(d) => { onChange(d ? toIsoDate(d) : ''); setOpen(false) }}
            disabled={[
              ...(min ? [{ before: parseIsoDate(min)! }] : []),
              ...(max ? [{ after: parseIsoDate(max)! }] : []),
            ]}
            captionLayout="dropdown"
            startMonth={parseIsoDate(min) ?? new Date(new Date().getFullYear() - 100, 0)}
            endMonth={parseIsoDate(max) ?? new Date(new Date().getFullYear() + 10, 11)}
          />
        </div>,
        document.body,
      )}
    </>
  )
}

/**
 * The same field for a `datetime-local` value: a calendar plus a time box.
 *
 * The value is `yyyy-MM-ddTHH:mm` — again exactly what the native input produced, so callers
 * that send it straight to the API keep working.
 */
export function DateTimePicker({
  value, onChange, disabled, min, max, id, className,
}: {
  value: string
  onChange: (isoDateTime: string) => void
  disabled?: boolean
  min?: string
  max?: string
  id?: string
  className?: string
}) {
  const [datePart, timePart] = value ? splitLocal(value) : ['', '']

  const setDate = (d: string) => {
    if (!d) { onChange(''); return }
    // A date with no time yet defaults to 09:00 rather than 00:00: these fields are
    // deadlines and appointments, and midnight is almost never what was meant.
    onChange(`${d}T${timePart || '09:00'}`)
  }
  const setTime = (t: string) => {
    if (!datePart) return
    onChange(`${datePart}T${t || '00:00'}`)
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <DatePicker
        id={id}
        value={datePart}
        onChange={setDate}
        disabled={disabled}
        min={min ? splitLocal(min)[0] : undefined}
        max={max ? splitLocal(max)[0] : undefined}
        className="flex-1"
      />
      <input
        type="time"
        value={timePart}
        onChange={(e) => setTime(e.target.value)}
        disabled={disabled || !datePart}
        aria-label="Time"
        className="erp-input w-28 shrink-0 disabled:opacity-50"
      />
    </div>
  )
}

// ── Date helpers ──────────────────────────────────────────────────────────────────────
//
// All local-time. See the note at the top of the file on why toISOString() is absent.

function parseIsoDate(s: string | undefined): Date | undefined {
  if (!s) return undefined
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return undefined
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function toIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function splitLocal(v: string): [string, string] {
  const parts = v.split('T')
  return [parts[0] ?? '', (parts[1] ?? '').slice(0, 5)]
}

function formatDisplay(d: Date): string {
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
